// src/components/Callback.jsx
import React, { useEffect, useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useUserQuery } from "../../../../hooks/api_hooks/useUserQuery";

const Callback = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const redirectStarted = useRef(false);
  const [refreshDone, setRefreshDone] = useState(false);
  const checkoutStarted = useRef(false);

  useEffect(() => {
    document.title = "Loading | Kloudscan";
  }, []);

  /* -------------------- Context detection -------------------- */
  const isCallback = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    return qs.has("code") && qs.has("state");
  }, []);

  const inIframe = useMemo(() => window.self !== window.top, []);

  const authBusy =
    auth.activeNavigator === "signinRedirect" ||
    auth.activeNavigator === "signinSilent";

  /* -------------------- TanStack query -------------------- */
  const {
    data: userData,
    isLoading: userLoading,
    isFetched: userFetched,
    refetch: refetchUser,
  } = useUserQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const hydrated = userFetched;

  const subscriptionStatus =
    userData?.user?.stripe_data?.subscription?.toLowerCase?.();
  const isSubscribed =
    subscriptionStatus === "active" || subscriptionStatus === "inactive";

  /* -------------------- 1️⃣ Refresh user data immediately once login completes -------------------- */
  useEffect(() => {
    const doRefresh = async () => {
      try {
        await refetchUser();
      } finally {
        setRefreshDone(true);
      }
    };

    if (auth.isAuthenticated && !refreshDone) {
      doRefresh();
    }
  }, [auth.isAuthenticated, refreshDone, refetchUser]);

  /* -------------------- 2️⃣ Handle redirect logic only after all data is ready -------------------- */
  useEffect(() => {
    if (inIframe || authBusy) return;

    // Not logged in? Begin login process
    if (!auth.isAuthenticated) {
      if (isCallback) return;
      if (!redirectStarted.current) {
        redirectStarted.current = true;
        auth.signinRedirect?.();
      }
      return;
    }

    // Wait for user hydration and refresh
    if (!refreshDone || userLoading || !hydrated) {
      return;
    }

    // Hold here — Stripe fetch already in flight, don't let routing logic run
    if (checkoutStarted.current) return;

    const user = userData?.user;

    // 💡 If user came from a plan selection, auto-launch Stripe checkout
    const pendingPriceId = localStorage.getItem("pending_price_id");
    if (pendingPriceId && !isSubscribed) {
      checkoutStarted.current = true;
      // Don't remove from localStorage until we have a valid URL

      fetch("https://api.kloudscan.com/stripe_checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.user?.access_token}`,
        },
        body: JSON.stringify({
          line_items: [{ price: pendingPriceId, quantity: 1 }],
          customer_email: auth.user?.profile?.email,
          username: auth.user?.profile?.sub,
        }),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Stripe response:", data);
          if (data.url) {
            localStorage.removeItem("pending_price_id"); // only clear on success
            localStorage.removeItem("login_intent");
            window.location.href = data.url;
          } else {
            navigate("/checkout", { replace: true });
          }
        })
        .catch((err) => {
          console.error("Stripe fetch failed:", err);
          navigate("/checkout", { replace: true });
        });

      return;
    }

    // 💡 Order of routing logic
    if (!isSubscribed) {
      navigate("/checkout", { replace: true });
      return;
    }

    if (!user?.personal_information) {
      navigate("/profile/personal-information", { replace: true });
      return;
    }

    if (!user?.authorization_signed) {
      navigate("/profile/authorization", { replace: true });
      return;
    }

    // ✅ Everything complete → dashboard
    navigate("/dashboard", { replace: true });
  }, [
    inIframe,
    authBusy,
    auth.isAuthenticated,
    auth.user,
    isCallback,
    userLoading,
    hydrated,
    isSubscribed,
    userData,
    refreshDone,
    navigate,
    auth,
  ]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f7f8fa",
      }}
    >
      <CircularProgress sx={{ color: "#12b2f1" }} size={40} thickness={4} />
    </Box>
  );
};

export default Callback;
